import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Video, Plus, Trash2, Edit2, X, ExternalLink, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEO_TYPES = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'ballercam', label: 'BallerCam' },
  { value: 'hudl', label: 'Hudl' },
  { value: 'zoom_recording', label: 'Zoom Recording' },
  { value: 'other', label: 'Other' },
];

const TeamVideos = () => {
  const { currentTeam, isManager } = useOutletContext();
  const [videos, setVideos] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterAthlete, setFilterAthlete] = useState('all');
  const [formData, setFormData] = useState({
    athlete_id: '',
    video_type: 'youtube',
    title: '',
    url: '',
    description: '',
    timestamp_notes: [{ time: '', note: '' }]
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('team_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    if (currentTeam) {
      fetchData();
    }
  }, [currentTeam]);

  const fetchData = async () => {
    try {
      // Fetch team athletes
      const athletesRes = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/athletes`,
        { headers: getAuthHeaders() }
      );
      const athletesData = athletesRes.ok ? await athletesRes.json() : [];
      setAthletes(athletesData);

      // Fetch team videos
      const videosRes = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/videos`,
        { headers: getAuthHeaders() }
      );
      const videosData = videosRes.ok ? await videosRes.json() : [];
      setVideos(videosData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        timestamp_notes: formData.timestamp_notes.filter(t => t.time.trim() || t.note.trim()),
        team_id: currentTeam.team_id
      };

      const url = editingVideo 
        ? `${API_URL}/api/clubs/team/${currentTeam.team_id}/videos/${editingVideo.id}`
        : `${API_URL}/api/clubs/team/${currentTeam.team_id}/videos`;
      
      const method = editingVideo ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingVideo ? 'Video updated' : 'Video added');
        closeForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save video');
      }
    } catch (error) {
      toast.error('Failed to save video');
    }
    setSubmitting(false);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      athlete_id: video.athlete_id || '',
      video_type: video.video_type,
      title: video.title,
      url: video.url,
      description: video.description || '',
      timestamp_notes: video.timestamp_notes?.length > 0 
        ? video.timestamp_notes 
        : [{ time: '', note: '' }]
    });
    setShowForm(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/videos/${videoId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success('Video deleted');
        fetchData();
      } else {
        toast.error('Failed to delete video');
      }
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingVideo(null);
    setFormData({
      athlete_id: '',
      video_type: 'youtube',
      title: '',
      url: '',
      description: '',
      timestamp_notes: [{ time: '', note: '' }]
    });
  };

  const addTimestampNote = () => {
    setFormData({
      ...formData,
      timestamp_notes: [...formData.timestamp_notes, { time: '', note: '' }]
    });
  };

  const updateTimestampNote = (index, field, value) => {
    const newNotes = [...formData.timestamp_notes];
    newNotes[index][field] = value;
    setFormData({ ...formData, timestamp_notes: newNotes });
  };

  const removeTimestampNote = (index) => {
    const newNotes = formData.timestamp_notes.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      timestamp_notes: newNotes.length > 0 ? newNotes : [{ time: '', note: '' }]
    });
  };

  const filteredVideos = filterAthlete === 'all' 
    ? videos 
    : videos.filter(v => v.athlete_id === filterAthlete);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Manager view - read only
  if (isManager) {
    return (
      <div className="space-y-6" data-testid="team-videos">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Video Analysis</h1>
          <p className="text-muted-foreground">{videos.length} videos</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-sm">
          <p className="text-sm text-blue-400">
            As a Team Manager, you can view videos but cannot upload or edit them.
            Contact your Team Coach to make changes.
          </p>
        </div>
        {videos.length === 0 ? (
          <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
            <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No videos uploaded yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video) => (
              <div key={video.id} className="bg-card border border-border/40 rounded-sm overflow-hidden">
                <div className="aspect-video bg-muted/30 flex items-center justify-center relative group">
                  <Play size={40} className="text-muted-foreground" />
                  <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ExternalLink className="text-white" size={24} />
                  </a>
                </div>
                <div className="p-4">
                  <h3 className="font-medium line-clamp-1">{video.title}</h3>
                  <p className="text-xs text-muted-foreground">{video.athlete_name || 'Team Video'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-videos">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Video Analysis</h1>
          <p className="text-muted-foreground">{videos.length} videos for {currentTeam?.team_name}</p>
        </div>
        <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Video
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <select
          value={filterAthlete}
          onChange={(e) => setFilterAthlete(e.target.value)}
          className="p-2 bg-transparent border border-border rounded-sm"
        >
          <option value="all" className="bg-card">All Athletes</option>
          {athletes.map(a => (
            <option key={a.id} value={a.id} className="bg-card">{a.name}</option>
          ))}
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">
                {editingVideo ? 'Edit Video' : 'Add Video'}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Athlete (Optional)</label>
                <select
                  value={formData.athlete_id}
                  onChange={(e) => setFormData({...formData, athlete_id: e.target.value})}
                  className="w-full p-3 bg-transparent border border-border rounded-sm"
                >
                  <option value="" className="bg-card">Team-wide Video</option>
                  {athletes.map(a => (
                    <option key={a.id} value={a.id} className="bg-card">{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Video Type</label>
                  <select
                    value={formData.video_type}
                    onChange={(e) => setFormData({...formData, video_type: e.target.value})}
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    {VIDEO_TYPES.map(type => (
                      <option key={type.value} value={type.value} className="bg-card">{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Title</label>
                  <Input
                    placeholder="Game vs Team X"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Video URL</label>
                <Input
                  placeholder="https://youtube.com/watch?v=..."
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  required
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Description</label>
                <Textarea
                  placeholder={"What to focus on in this video…\n\nTip: press Enter for a new line. Use - or • for bullets."}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={6}
                  className="w-full resize-y font-normal leading-relaxed"
                />
              </div>

              {/* Timestamp Notes */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Timestamp Notes</label>
                {formData.timestamp_notes.map((note, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      placeholder="0:00"
                      value={note.time}
                      onChange={(e) => updateTimestampNote(idx, 'time', e.target.value)}
                      className="w-20"
                    />
                    <Input
                      placeholder="Note at this timestamp..."
                      value={note.note}
                      onChange={(e) => updateTimestampNote(idx, 'note', e.target.value)}
                      className="flex-1"
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeTimestampNote(idx)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addTimestampNote}>
                  <Plus size={14} className="mr-1" /> Add Timestamp
                </Button>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                  {submitting ? 'Saving...' : editingVideo ? 'Update' : 'Add Video'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Videos List */}
      {filteredVideos.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            {filterAthlete === 'all' ? 'No videos yet' : 'No videos for this athlete'}
          </p>
          <Button onClick={() => setShowForm(true)}>Add Your First Video</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos.map((video) => (
            <div key={video.id} className="bg-card border border-border/40 rounded-sm overflow-hidden">
              {/* Video Preview Placeholder */}
              <div className="aspect-video bg-muted/30 flex items-center justify-center relative group">
                <Play size={40} className="text-muted-foreground" />
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ExternalLink className="text-white" size={24} />
                </a>
              </div>
              
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium line-clamp-1">{video.title}</h3>
                    <p className="text-xs text-muted-foreground">{video.athlete_name || 'Team Video'}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(video)} className="p-1 hover:bg-muted rounded-sm">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(video.id)} className="p-1 hover:bg-muted rounded-sm text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">
                    {VIDEO_TYPES.find(t => t.value === video.video_type)?.label || video.video_type}
                  </span>
                </div>

                {video.description && (
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed line-clamp-6 mb-2">{video.description}</p>
                )}

                {video.timestamp_notes?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {video.timestamp_notes.length} timestamp note(s)
                  </p>
                )}

                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
                >
                  <ExternalLink size={12} />
                  Watch Video
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamVideos;
